import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Stack, Typography, TextField, Button, InputAdornment,
  IconButton, Alert, Collapse, CircularProgress, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { GlobalContext } from '../navigation/ContextProvider';
import { loginApi, projectname } from '../utils/Apis';
import { Constants } from '../utils/Constants';

const Login = () => {
  const { setGlobalAccess, setGlobalAccessType } = React.useContext(GlobalContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [phone, setphone] = React.useState('');
  const [password, setpassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, seterror] = React.useState(false);
  const [message, setmessage] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [touched, setTouched] = React.useState({ phone: false, password: false });

  // Data flow preserved exactly from the original implementation.
  const submit = async () => {
    setTouched({ phone: true, password: true });
    if (phone === '' || password === '') {
      seterror(true);
      setmessage('Please complete the full form');
      return;
    }
    seterror(false);
    setLoading(true);
    // client:'panel' — the panel session lives alongside the phone/desktop app session.
    loginApi('login', { slug_name: phone, password: password, client: 'panel' })
      .then((result) => {
        setLoading(false);
        if (result && result.error === 0) {
          localStorage.setItem(Constants.APP_KEY, result.token);
          localStorage.setItem(Constants.USERTYPE, result.data);
          localStorage.setItem(Constants.USERID, result?.user_id);
          setGlobalAccess(true);
          if (result.data === 'SA') {
            setGlobalAccessType(result.data);
            navigate('/admin_list');
          } else if (result.data === 'Admin' || result.data === 'SubAdmin') {
            // SubAdmin = host: same pages, limited to the participants/recordings of their rooms.
            setGlobalAccessType(result.data);
            navigate('/sub_admin_list');
          }
        } else {
          seterror(true);
          setmessage((result && (result.status || result.message)) || 'Invalid credentials. Please try again.');
        }
      })
      .catch(() => {
        setLoading(false);
        seterror(true);
        setmessage('Unable to sign in. Please check your connection and try again.');
      });
  };

  const onKeyDown = (e) => { if (e.key === 'Enter') submit(); };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default', position: 'relative' }}>
      <div className="aurora" />
      <div className="grid-veil" />
      {/* Brand panel */}
      {isDesktop && (
        <Box
          sx={{
            flex: 1, position: 'relative', color: '#fff', p: 7,
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            background: 'radial-gradient(120% 90% at 15% 0%,#38bdf8 0%,#2563eb 40%,#312e81 100%)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)', top: -120, right: -120 }} />
          <Box sx={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', bottom: -100, left: -60 }} />
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ position: 'relative' }}>
            <Box component="img" src={process.env.PUBLIC_URL + '/zproaudio.png'} alt="" sx={{ width: 42, height: 42, borderRadius: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '0.04em' }}>{projectname}</Typography>
          </Stack>
          <Box sx={{ position: 'relative' }}>
            <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 2 }}>
              Manage your meetings,<br />rooms and users — all in one place.
            </Typography>
            <Typography sx={{ opacity: 0.85, maxWidth: 440 }}>
              Sign in to the {projectname} admin console to manage hosts, rooms, participants and recordings.
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.7, position: 'relative' }}>
            © {new Date().getFullYear()} {projectname}. All rights reserved.
          </Typography>
        </Box>
      )}

      {/* Form panel */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, sm: 6 }, position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          className="rise-in"
          sx={{
            width: '100%', maxWidth: 420, p: { xs: 3, sm: 5 }, borderRadius: 4,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
          }}
        >
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="h3">Sign in</Typography>
            <Typography color="text.secondary">Welcome back. Please enter your details.</Typography>
          </Stack>

          <Collapse in={error} sx={{ mb: error ? 2 : 0 }}>
            <Alert severity="error" onClose={() => seterror(false)}>{message}</Alert>
          </Collapse>

          <Stack spacing={2.5}>
            <TextField
              label="Username"
              placeholder="Enter your username"
              value={phone}
              onChange={(e) => setphone(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
              onKeyDown={onKeyDown}
              error={touched.phone && phone === ''}
              helperText={touched.phone && phone === '' ? 'Username is required' : ' '}
              InputProps={{ startAdornment: (<InputAdornment position="start"><PersonOutlineRoundedIcon fontSize="small" /></InputAdornment>) }}
            />
            <TextField
              label="Password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setpassword(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, password: true }))}
              onKeyDown={onKeyDown}
              error={touched.password && password === ''}
              helperText={touched.password && password === '' ? 'Password is required' : ' '}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment>),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small" aria-label="toggle password visibility">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained" size="large" onClick={submit} disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ py: 1.25 }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            By signing in, you agree to our Terms and Conditions & Privacy Policy.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default Login;
